import Box from "@mui/material/Box";
import {
    EventNameMaxCharacter,
    FAILED_ACTION_MESSAGE,
    FETCH_GATEWAY_FAIL_MESSAGE,
    Patterns,
    UNGROUPED_NAME,
} from "constant";
import { get } from "lodash";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Button, Form, FormControl, Modal } from "react-bootstrap";

import { useHistory } from "react-router-dom";
import EventService from "service/eventService";
import { getRegistryGroups } from "service/gatewayService";
import { Group, Registry } from "types/Gateways";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { isHttpSuccess } from "utils/functions";

export interface NewEventModalProps {
    show: boolean;
    onClose?: () => void;
}

export const NewEventModal = ({ show, onClose }: NewEventModalProps) => {
    const history = useHistory();

    const [newEvent, setNewEvent] = useState({
        eventName: "",
        isGlobal: true,
        selectedGatewayId: "",
    });
    const [gatewayGroups, setGatewayGroups] = useState<Group[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Fetch all gateways
     */
    const fetchAllGateways = useCallback(async () => {
        try {
            setIsLoading(true);
            const gwgroup = await getRegistryGroups();
            if (!isHttpSuccess(gwgroup.status)) {
                showErrorAlert(
                    getAPIError(gwgroup, FETCH_GATEWAY_FAIL_MESSAGE)
                );
                return;
            }
            const groups: Group[] = get(gwgroup, "data", []);
            const onboardedGroups = groups.map((group) => ({
                ...group,
                gateways: group.gateways.filter(
                    (gateway) => gateway.info
                ),
            }));
            setGatewayGroups(onboardedGroups);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (show) {
            fetchAllGateways();
        }
        setNewEvent({
            eventName: "",
            isGlobal: true,
            selectedGatewayId: "",
        });
    }, [show, fetchAllGateways]);

    const handleOnClose = () => {
        if (isLoading) {
            return;
        }

        onClose?.();
    };

    const handleCreateEvent = async () => {
        try {
            setIsLoading(true);
            const trimmedEventName = newEvent.eventName.trim();

            if (!Patterns.eventNamePattern.test(trimmedEventName) || trimmedEventName.length > EventNameMaxCharacter) {
                showErrorAlert({
                    title: "Invalid Event Name",
                    message: `Event name must be 1-${EventNameMaxCharacter} characters long and not contain leading or trailing spaces or non-English characters.`,
                });
                setIsLoading(false);
                return;
            }

            const response: any = await EventService.createEvent(
                newEvent.eventName,
                newEvent.isGlobal ? undefined : newEvent.selectedGatewayId
            );
            if (isHttpSuccess(response.status)) {
                showSuccessAlert({
                    message: get(
                        response,
                        "message",
                        "New Event has been created successfully."
                    ),
                });
                history.push(`/event-details/${response.data.uuid}`);
            } else {
                showErrorAlert(getAPIError(response, FAILED_ACTION_MESSAGE));
                setIsLoading(false);
            }
        } catch {
            showErrorAlert({
                title: "Failed to create event",
                message: FAILED_ACTION_MESSAGE,
            });
            setIsLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleOnClose}
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <Modal.Header>
                <Modal.Title>New Event</Modal.Title>
                <Button
                    variant=""
                    className="close-button"
                    onClick={handleOnClose}
                >
                    <span className="material-icons">close</span>
                </Button>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <FormControl
                        type="text"
                        placeholder="Event Name"
                        autoFocus
                        value={newEvent.eventName || ""}
                        onChange={(e) => {
                            setNewEvent({
                                ...newEvent,
                                eventName: e.target.value,
                            });
                        }}
                    ></FormControl>

                    <p
                        style={{
                            color: "#828FAB",
                            fontSize: 18,
                            fontWeight: 500,
                            marginTop: "1rem",
                            marginBottom: "0.5rem",
                        }}
                    >
                        Select Event Type
                    </p>

                    <Box style={{ height: "200px" }}>
                        <Box className="modal-option">
                            <Form.Check
                                aria-label="select-global-event"
                                name="selected-global-event"
                                id="global-event"
                                custom
                                type="radio"
                                value="global"
                                checked={newEvent.isGlobal}
                                onChange={(e) => {
                                    setNewEvent({
                                        ...newEvent,
                                        isGlobal: true,
                                    });
                                }}
                                label={
                                    <div>
                                        <h4 className="select-gw-name">
                                            Global
                                        </h4>{" "}
                                        <p className="select-gw-id">
                                            Runs in the cloud, managing devices
                                            across all gateways. It supports a
                                            full range of actions including
                                            modify Event variables.
                                        </p>
                                    </div>
                                }
                                className="float-left"
                            />
                        </Box>

                        <Box className="modal-option">
                            <Form.Check
                                aria-label="select-gateway-level-checkbox"
                                name="selected-gateway-level-event"
                                id="gateway-level-event"
                                custom
                                type="radio"
                                value="gateway"
                                checked={!newEvent.isGlobal}
                                onChange={(e) => {
                                    setNewEvent({
                                        ...newEvent,
                                        isGlobal: false,
                                    });
                                }}
                                label={
                                    <div>
                                        <h4 className="select-gw-name">
                                            Gateway Based Event
                                        </h4>{" "}
                                        <p className="select-gw-id">
                                            Operates locally on a specific
                                            gateway, monitoring and controlling
                                            only its connected devices. It
                                            offers faster response but excludes
                                            actions like modify Event variables.
                                        </p>
                                    </div>
                                }
                                className="float-left"
                            />
                        </Box>
                    </Box>

                    {!newEvent.isGlobal && (
                        <>
                            <p
                                style={{
                                    color: "#828FAB",
                                    fontSize: 18,
                                    fontWeight: 500,
                                    marginTop: "1rem",
                                    marginBottom: "0.5rem",
                                }}
                            >
                                Select Gateway
                            </p>

                            <Box
                                style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                }}
                            >
                                {gatewayGroups
                                    .filter(
                                        (group) =>
                                            group.name !== UNGROUPED_NAME &&
                                            group.uuid &&
                                            group.gateways.length > 0
                                    )
                                    .map((group) => (
                                        <Fragment key={group.uuid}>
                                            <p
                                                style={{
                                                    fontSize: 18,
                                                    fontWeight: 500,
                                                    marginTop: "1rem",
                                                    marginBottom: "0.5rem",
                                                }}
                                            >
                                                {group.name}
                                            </p>

                                            {group.gateways.map(
                                                (gateway: Registry) => (
                                                    <Box
                                                        className="modal-option"
                                                        key={gateway.gateway_id}
                                                    >
                                                        <Form.Check
                                                            aria-label="select-gateway-checkbox"
                                                            name="selected-gateway"
                                                            id={
                                                                gateway.gateway_id
                                                            }
                                                            custom
                                                            type="radio"
                                                            value={
                                                                gateway.gateway_id
                                                            }
                                                            checked={
                                                                newEvent.selectedGatewayId ===
                                                                gateway.gateway_id
                                                            }
                                                            onChange={(e) => {
                                                                setNewEvent({
                                                                    ...newEvent,
                                                                    selectedGatewayId:
                                                                        e.target
                                                                            .value,
                                                                });
                                                            }}
                                                            label={
                                                                <div>
                                                                    <h4 className="select-gw-name">
                                                                        {
                                                                            gateway.name
                                                                        }
                                                                    </h4>
                                                                    <p className="select-gw-id">
                                                                        UUID:{" "}
                                                                        {
                                                                            gateway.gateway_id
                                                                        }
                                                                    </p>
                                                                </div>
                                                            }
                                                            className="float-left"
                                                        />
                                                    </Box>
                                                )
                                            )}
                                        </Fragment>
                                    ))}

                                {gatewayGroups
                                    .filter(
                                        (group) =>
                                            group.name === UNGROUPED_NAME &&
                                            !group.uuid &&
                                            group.gateways.length > 0
                                    )
                                    .map((group, index) => (
                                        <Fragment key={`standalone-group`}>
                                            <p
                                                style={{
                                                    fontSize: 18,
                                                    fontWeight: 500,
                                                    marginTop: "1rem",
                                                    marginBottom: "0.5rem",
                                                }}
                                            >
                                                Standalone
                                            </p>

                                            {group.gateways.map(
                                                (gateway: Registry) => (
                                                    <Box
                                                        className="modal-option"
                                                        key={gateway.gateway_id}
                                                    >
                                                        <Form.Check
                                                            aria-label="select-gateway-checkbox"
                                                            name="selected-gateway"
                                                            id={
                                                                gateway.gateway_id
                                                            }
                                                            custom
                                                            type="radio"
                                                            value={
                                                                gateway.gateway_id
                                                            }
                                                            checked={
                                                                newEvent.selectedGatewayId ===
                                                                gateway.gateway_id
                                                            }
                                                            onChange={(e) => {
                                                                setNewEvent({
                                                                    ...newEvent,
                                                                    selectedGatewayId:
                                                                        e.target
                                                                            .value,
                                                                });
                                                            }}
                                                            label={
                                                                <div>
                                                                    <h4 className="select-gw-name">
                                                                        {
                                                                            gateway.name
                                                                        }
                                                                    </h4>
                                                                    <p className="select-gw-id">
                                                                        UUID:{" "}
                                                                        {
                                                                            gateway.gateway_id
                                                                        }
                                                                    </p>
                                                                </div>
                                                            }
                                                            className="float-left"
                                                        />
                                                    </Box>
                                                )
                                            )}
                                        </Fragment>
                                    ))}
                            </Box>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    className="btn-create"
                    onClick={handleCreateEvent}
                    disabled={
                        !newEvent.eventName ||
                        (!newEvent.isGlobal && !newEvent.selectedGatewayId) ||
                        isLoading
                    }
                >
                    CREATE
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
